import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import SpecialitiesPopup from "./SpecialitiesPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const SpecialitiesWidget = ({ setFieldValue, values, errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Plumbing skills data (same as popup)
  const plumbingSkills = [
    { id: 1, name: "Pipe Installation" },
    { id: 2, name: "Backflow Prevention" },
    { id: 3, name: "Drain Cleaning" },
    { id: 4, name: "Water Heater" },
    { id: 5, name: "Fixture Installation" },
    { id: 6, name: "Gas Line Work" },
    { id: 7, name: "Sewer Line Repair" },
    { id: 8, name: "Emergency Plumbing" },
    { id: 9, name: "Cold Water System" },
    { id: 10, name: "Tap Repair" },
    { id: 11, name: "Toilet Installation" },
    { id: 12, name: "Shower Installation" },
    { id: 13, name: "Bath Installation" },
    { id: 14, name: "Sink Installation" },
    { id: 15, name: "Leak Repair" },
    { id: 16, name: "Washing Machine" },
    { id: 17, name: "Hot Water System" },
    { id: 18, name: "Dishwasher Installation" }
  ];

  // Define fields with estimated completion times
  const fields = [
    { name: "Featured Services", key: "specialities", estimatedTime: "2 min", required: false }
  ];

  // Sample data for demonstration
  const sampleData = {
    specialities: [1, 2, 3] // Using numeric IDs to match plumbing skills
  };

  // Initialize form values with sample data if empty
  React.useEffect(() => {
    if (!values.specialities || values.specialities.length === 0) {
      setFieldValue("specialities", sampleData.specialities);
    }
  }, [values.specialities, setFieldValue]);

  // Merge sample data with actual values (use sample data when form values are empty)
  const displayValues = {
    specialities: values.specialities || sampleData.specialities || []
  };

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      // Check if field has a value (either from form or sample data)
      const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
      
      // Only consider fields completed if they actually have a value
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  // Get selected specialities labels for display
  const getSelectedSpecialitiesLabels = () => {
    const selectedValues = displayValues.specialities || [];
    return selectedValues.map(value => {
      const skill = plumbingSkills.find(skill => skill.id === value);
      return skill ? skill.name : value;
    });
  };

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Featured Services</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key].length > 0;
              const isCompleted = hasValue; // Only completed if it actually has a value
              
              return (
                <div key={field.key} className="flex items-center justify-between">
                    {progress === 100 ? (
                      <div 
                        className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className="text-offblack truncate">
                          {isCompleted ? getSelectedSpecialitiesLabels().join(", ") : `Estimated ${field.estimatedTime}`}
                        </AdminMainText>
                      </div>
                    ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? getSelectedSpecialitiesLabels().join(", ") : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <SpecialitiesPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          businessDashboard={true}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default SpecialitiesWidget;
